import { Select as MantineSelect, SelectProps, useMantineTheme } from '@mantine/core';
import { forwardRef } from 'react';
import { usePreferencesStore } from '../../../stores';
import { defaultColorScheme } from '../../../theme/theme';
import { LINE_HEIGHT_MAP, LETTER_SPACING_MAP } from '../../../hooks/useAccessibilityStyles';

export type GradientVariant =
  | 'primary'
  | 'secondary'
  | 'tertiary'
  | 'success'
  | 'info'
  | 'warning'
  | 'danger';

export interface ThemedSelectProps extends SelectProps {
  gradientFocus?: boolean;
  gradientVariant?: GradientVariant;
  useColorScheme?: boolean; // Use color scheme from state management
}

const Select = forwardRef<HTMLInputElement, ThemedSelectProps>(
  ({ gradientFocus = true, gradientVariant = 'primary', useColorScheme = true, styles, ...rest }, ref) => {
    const theme = useMantineTheme();
    const colorSchemeKey = usePreferencesStore((state) => state.colorSchemeKey);
    const reducedMotion = usePreferencesStore((state) => state.reducedMotion);
    const strongFocus = usePreferencesStore((state) => state.strongFocus);
    const lineSpacing = usePreferencesStore((state) => state.lineSpacing);
    const letterSpacingPref = usePreferencesStore((state) => state.letterSpacing);

    const accessibilityStyles = {
      lineHeight: LINE_HEIGHT_MAP[lineSpacing],
      letterSpacing: LETTER_SPACING_MAP[letterSpacingPref],
    };

    // Determine which color to use
    let color = theme.other.gradients[gradientVariant].from;

    if (useColorScheme) {
      const effectiveColorSchemeKey = colorSchemeKey || defaultColorScheme.key;
      const colorScheme = theme.other.colorSchemes?.find((cs: any) => cs.key === effectiveColorSchemeKey);
      if (colorScheme) {
        color = colorScheme.primary.from;
      }
    }

    if (!gradientFocus) {
      return (
        <MantineSelect
          ref={ref}
          styles={{
            ...styles,
            label: {
              textAlign: 'left !important' as any,
              ...accessibilityStyles,
              ...(styles as any)?.label,
            },
            input: {
              paddingLeft: rest.leftSection ? '40px' : undefined,
              paddingRight: rest.rightSection ? '40px' : undefined,
              ...accessibilityStyles,
              ...(styles as any)?.input,
            },
            option: {
              ...accessibilityStyles,
              ...(styles as any)?.option,
            },
          }}
          {...rest}
        />
      );
    }

    return (
      <MantineSelect
        ref={ref}
        styles={{
          ...styles,
          label: {
            textAlign: 'left !important' as any,
            ...accessibilityStyles,
            ...(styles as any)?.label,
          },
          input: {
            paddingLeft: rest.leftSection ? '40px' : undefined,
            paddingRight: rest.rightSection ? '40px' : undefined,
            transition: reducedMotion ? 'none' : 'border-color 0.2s ease, box-shadow 0.2s ease',
            '&:focus': {
              borderColor: `${color} !important`,
              boxShadow: strongFocus ? `0 0 0 3px ${color}50 !important` : `0 0 0 2px ${color}30 !important`,
              outline: strongFocus ? `2px solid ${color} !important` : 'none',
              outlineOffset: strongFocus ? '2px' : '0',
            },
            '&:focus-within': {
              borderColor: `${color} !important`,
            },
            ...accessibilityStyles,
            ...(styles as any)?.input,
          },
          option: {
            '&[data-selected]': {
              backgroundColor: `${color} !important`,
              color: 'white !important',
            },
            '&[data-hovered]': {
              backgroundColor: `${color}20 !important`,
            },
            ...accessibilityStyles,
            ...(styles as any)?.option,
          },
        }}
        {...rest}
      />
    );
  }
);

Select.displayName = 'Select';

export default Select;
